import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Divider,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
  FormGroup,
  FormHelperText,
  Tooltip,
  IconButton,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useStore } from "@xyflow/react";

const filenamePlaceholders = [
  { value: "{date}", label: "Current Date" },
  { value: "{time}", label: "Current Time" },
  { value: "{datetime}", label: "Date and Time" },
  { value: "{nodeid}", label: "Node ID" },
  { value: "{reportname}", label: "Report Name" },
];

const exportFormats = [
  { value: "excel", label: "Excel (.xlsx)" },
  { value: "csv", label: "CSV (.csv)" },
];

export const ExcelExportNodeModal = ({ open, node, selectedInputId: parentSelectedInputId }) => {
  // Error handling for null/undefined node
  if (!node) {
    console.error("ExcelExportNodeModal: Received null or undefined node");
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Error: Unable to load node data</Typography>
      </Box>
    );
  }

  const nodeData = node?.data || {};

  // Multi-input state management
  const [upstreamSources, setUpstreamSources] = useState([]);
  const selectedInputId = parentSelectedInputId; // Use parent's selectedInputId
  const [fieldsPerInput, setFieldsPerInput] = useState({});
  const [exportFormat, setExportFormat] = useState("excel");
  const [downloadAsFile, setDownloadAsFile] = useState(true);
  const [filenameTemplate, setFilenameTemplate] = useState("export-{datetime}");
  const [includeHeaders, setIncludeHeaders] = useState(true);
  const [autoFormat, setAutoFormat] = useState(true);
  const [applyStyles, setApplyStyles] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [error, setError] = useState(null);

  // Simple refs for initialization tracking
  const initializedRef = useRef(false);
  const lastInitializedNodeId = useRef(null);
  const autoSelectedFieldsRef = useRef(false);
  const lastPersistedSettingsRef = useRef(null);

  // Get store data with error handling
  const nodes = useStore((s) => {
    try {
      return s.nodes || [];
    } catch (error) {
      console.error("Error accessing nodes from store:", error);
      return [];
    }
  });

  const edges = useStore((s) => {
    try {
      return s.edges || [];
    } catch (error) {
      console.error("Error accessing edges from store:", error);
      return [];
    }
  });

  // Get all upstream data sources
  useEffect(() => {
    if (!open || !node?.id) return;

    try {
      // Find all edges where this node is the target
      const incomingEdges = edges.filter((e) => e.target === node?.id);

      if (incomingEdges.length === 0) {
        setUpstreamSources([]);
        setSelectedInputId(null);
        return;
      }

      const sources = incomingEdges
        .map((edge) => {
          const sourceNode = nodes.find((n) => n.id === edge.source);
          if (!sourceNode) return null;

          let foundData = null;
          let displayName =
            sourceNode.data?.name || sourceNode.data?.label || `Node ${sourceNode.id}`;

          // Extract data based on node type
          if (sourceNode.data?.type === "data.model" || sourceNode.type === "data.model") {
            // Check execution results first (most up-to-date)
            if (sourceNode.data?.executionState?.preview?.results) {
              foundData = { results: sourceNode.data.executionState.preview.results };
            }
            // Check for selectedSchema in output (this is how model nodes store their data)
            else if (sourceNode.data?.output?.selectedSchema) {
              foundData = { selectedSchema: sourceNode.data.output.selectedSchema };
            }
            // Check output schema (fallback)
            else if (sourceNode.data?.output?.schema) {
              foundData = { schema: sourceNode.data.output.schema };
            }
            // Then check model schema
            else if (sourceNode.data?.model?.schema) {
              foundData = { schema: sourceNode.data.model.schema };
            }
            // Check if there's raw data
            else if (sourceNode.data?.data && Array.isArray(sourceNode.data.data)) {
              foundData = { data: sourceNode.data.data };
            }
          } else if (sourceNode.data?.type === "data.filter" || sourceNode.type === "data.filter") {
            if (sourceNode.data?.executionState?.preview?.results) {
              foundData = { results: sourceNode.data.executionState.preview.results };
            } else if (sourceNode.data?.output?.results) {
              foundData = { results: sourceNode.data.output.results };
            } else if (sourceNode.data?.output?.settings) {
              let filterData = { settings: sourceNode.data.output.settings };
              // Get schema from connected model
              const filterInputEdge = edges.find((e) => e.target === sourceNode.id);
              if (filterInputEdge) {
                const filterInputNode = nodes.find((n) => n.id === filterInputEdge.source);
                if (filterInputNode?.data?.output?.selectedSchema) {
                  filterData.selectedSchema = filterInputNode.data.output.selectedSchema;
                } else if (filterInputNode?.data?.model?.schema) {
                  filterData.schema = filterInputNode.data.model.schema;
                }
              }
              foundData = filterData;
            }
          }

          if (!foundData && sourceNode.data?.output) {
            foundData = sourceNode.data.output;
          }

          return foundData
            ? {
                id: edge.source,
                nodeId: edge.source,
                displayName,
                data: foundData,
                nodeType: sourceNode.data?.type || sourceNode.type,
                edgeId: edge.id,
              }
            : null;
        })
        .filter(Boolean);

      setUpstreamSources(sources);
    } catch (error) {
      console.error("Error getting upstream sources:", error);
      setUpstreamSources([]);
    }
  }, [open, node?.id, edges, nodes]);

  // Get current upstream data based on selected input
  const currentUpstreamData = useMemo(() => {
    if (!selectedInputId) return null;
    const source = upstreamSources.find((s) => s.id === selectedInputId);
    return source?.data || null;
  }, [upstreamSources, selectedInputId]);

  // Extract fields for current input
  const [availableFields, setAvailableFields] = useState([]);
  useEffect(() => {
    if (!currentUpstreamData || !selectedInputId) {
      setAvailableFields([]);
      return;
    }

    try {
      let extractedFields = [];

      // Same field extraction logic as before
      if (
        currentUpstreamData?.settings?.fieldsToInclude &&
        Array.isArray(currentUpstreamData.settings.fieldsToInclude)
      ) {
        extractedFields = currentUpstreamData.settings.fieldsToInclude.map((fieldName) => ({
          key: fieldName,
          label: fieldName,
          type: "string",
        }));
      } else if (currentUpstreamData?.selectedSchema?.properties) {
        extractedFields = Object.entries(currentUpstreamData.selectedSchema.properties).map(
          ([key, val]) => ({
            key,
            label: key,
            type: val.type || "string",
            format: val.format,
            reference: val.reference,
          })
        );
      } else if (currentUpstreamData?.schema?.properties) {
        extractedFields = Object.entries(currentUpstreamData.schema.properties).map(
          ([key, val]) => ({
            key,
            label: val.label || val.title || key,
            type: val.type || "string",
            format: val.format,
          })
        );
      } else if (
        currentUpstreamData?.results &&
        Array.isArray(currentUpstreamData.results) &&
        currentUpstreamData.results.length > 0
      ) {
        const firstRow = currentUpstreamData.results[0];
        if (firstRow && typeof firstRow === "object") {
          extractedFields = Object.keys(firstRow).map((key) => ({
            key,
            label: key,
            type:
              typeof firstRow[key] === "number"
                ? "number"
                : typeof firstRow[key] === "boolean"
                  ? "boolean"
                  : "string",
          }));
        }
      } else if (
        currentUpstreamData?.data &&
        Array.isArray(currentUpstreamData.data) &&
        currentUpstreamData.data.length > 0
      ) {
        const firstRow = currentUpstreamData.data[0];
        if (firstRow && typeof firstRow === "object") {
          extractedFields = Object.keys(firstRow).map((key) => ({
            key,
            label: key,
            type:
              typeof firstRow[key] === "number"
                ? "number"
                : typeof firstRow[key] === "boolean"
                  ? "boolean"
                  : "string",
          }));
        }
      } else if (
        currentUpstreamData?.inferredFields &&
        Array.isArray(currentUpstreamData.inferredFields)
      ) {
        extractedFields = currentUpstreamData.inferredFields;
      }

      setAvailableFields(extractedFields);
    } catch (error) {
      console.error("Error extracting fields:", error);
      setAvailableFields([]);
    }
  }, [currentUpstreamData, selectedInputId]);

  // Auto-select fields for new inputs (separate effect to avoid loops)
  useEffect(() => {
    if (availableFields.length > 0 && selectedInputId && !fieldsPerInput[selectedInputId]) {
      // Default to "ALL" instead of individual fields
      setFieldsPerInput((prev) => ({
        ...prev,
        [selectedInputId]: ["ALL"],
      }));
    }
  }, [availableFields, selectedInputId, fieldsPerInput]);

  // Get current fields for selected input
  const currentFieldsToInclude = fieldsPerInput[selectedInputId] || [];

  // Debug: Track when currentFieldsToInclude changes
  useEffect(() => {}, [
    currentFieldsToInclude,
    selectedInputId,
    fieldsPerInput,
    availableFields.length,
  ]);

  // Handle field changes for current input
  const handleFieldsChange = useCallback(
    (_, newValue) => {
      if (!selectedInputId) return;

      // Extract field keys from the new value
      let newFields = newValue
        .map((v) => {
          // Handle both object format { key: "fieldName", label: "Label" } and string format
          if (typeof v === "object" && v !== null) {
            return v.key || v.label || v;
          }
          return v;
        })
        .filter(Boolean);

      // Handle "ALL" selection logic
      const ALL_OPTION = "ALL";
      const currentFields = fieldsPerInput[selectedInputId] || [];

      if (newFields.includes(ALL_OPTION)) {
        // If "ALL" was just added (wasn't in previous selection)
        if (!currentFields.includes(ALL_OPTION)) {
          newFields = [ALL_OPTION]; // Only "ALL"
        } else {
          // If "ALL" was already selected and other fields are being added, remove "ALL"
          newFields = newFields.filter((f) => f !== ALL_OPTION);
        }
      }

      setFieldsPerInput((prev) => ({
        ...prev,
        [selectedInputId]: newFields,
      }));
    },
    [selectedInputId, fieldsPerInput]
  );

  // Initialize settings when modal opens or node changes
  useEffect(() => {
    try {
      if (!open || !node?.id) return;

      if (!initializedRef.current || lastInitializedNodeId.current !== node?.id) {
        if (!node?.data) {
          initializedRef.current = true;
          lastInitializedNodeId.current = node?.id;
          return;
        }

        const exportSettings = node.data?.output?.settings || {};
        const defaults = {
          exportFormat: "excel",
          downloadAsFile: true,
          filenameTemplate: "export-{datetime}",
          includeHeaders: true,
          autoFormat: true,
          applyStyles: true,
        };

        // Restore fields per input - prioritize original state over expanded
        if (exportSettings.originalInputSources) {
          // Use original selection state if available (preserves "ALL" selections)
          setFieldsPerInput(exportSettings.originalInputSources);
        } else if (exportSettings.inputSources) {
          // Fallback to expanded sources for backward compatibility
          setFieldsPerInput(exportSettings.inputSources);
        }

        setExportFormat(exportSettings.exportFormat || defaults.exportFormat);
        setDownloadAsFile(exportSettings.downloadAsFile !== false);
        setFilenameTemplate(exportSettings.filenameTemplate || defaults.filenameTemplate);
        setIncludeHeaders(exportSettings.includeHeaders !== false);
        setAutoFormat(exportSettings.autoFormat !== false);
        setApplyStyles(exportSettings.applyStyles !== false);

        setError(null);
        initializedRef.current = true;
        lastInitializedNodeId.current = node?.id;
      }
    } catch (error) {
      console.error("Error initializing Excel export settings:", error);
      initializedRef.current = true;
      lastInitializedNodeId.current = node?.id;
    }

    if (!open) {
      initializedRef.current = false;
      lastInitializedNodeId.current = null;
    }
  }, [open, node?.id]);

  // Insert placeholder into filename template at cursor position
  const insertPlaceholder = (placeholder) => {
    setFilenameTemplate((prevTemplate) => {
      return prevTemplate + placeholder;
    });
  };
  // Persist settings to node data
  const updateNodeSettings = useCallback(() => {
    try {
      if (!node?.data?.setData) return;

      // Keep both original and expanded selections
      const expandedInputSources = {};
      const originalInputSources = {}; // Store original selection state

      Object.entries(fieldsPerInput).forEach(([inputId, fields]) => {
        // Store original selection (including "ALL")
        originalInputSources[inputId] = fields;

        if (fields.includes("ALL")) {
          // Find the available fields for this input and expand "ALL"
          const inputSource = upstreamSources.find((s) => s.id === inputId);
          if (inputSource) {
            // Get available fields for this specific input
            let inputAvailableFields = [];

            // Use the same logic as the field extraction
            const currentUpstreamData = inputSource.data;
            if (
              currentUpstreamData?.settings?.fieldsToInclude &&
              Array.isArray(currentUpstreamData.settings.fieldsToInclude)
            ) {
              inputAvailableFields = currentUpstreamData.settings.fieldsToInclude;
            } else if (
              currentUpstreamData?.results &&
              Array.isArray(currentUpstreamData.results) &&
              currentUpstreamData.results.length > 0
            ) {
              const firstRow = currentUpstreamData.results[0];
              if (firstRow && typeof firstRow === "object") {
                inputAvailableFields = Object.keys(firstRow);
              }
            } else if (
              currentUpstreamData?.data &&
              Array.isArray(currentUpstreamData.data) &&
              currentUpstreamData.data.length > 0
            ) {
              const firstRow = currentUpstreamData.data[0];
              if (firstRow && typeof firstRow === "object") {
                inputAvailableFields = Object.keys(firstRow);
              }
            } else if (
              currentUpstreamData?.inferredFields &&
              Array.isArray(currentUpstreamData.inferredFields)
            ) {
              inputAvailableFields = currentUpstreamData.inferredFields.map((f) => f.key);
            }

            expandedInputSources[inputId] = inputAvailableFields;
          } else {
            expandedInputSources[inputId] = availableFields.map((f) => f.key);
          }
        } else {
          expandedInputSources[inputId] = fields;
        }
      });

      const settings = {
        inputSources: expandedInputSources, // Save expanded fields for execution
        originalInputSources: originalInputSources, // Save original state for UI restoration
        exportFormat,
        downloadAsFile,
        filenameTemplate,
        includeHeaders,
        autoFormat,
        applyStyles,
      };

      const last = lastPersistedSettingsRef.current;
      const settingsChanged = !last || JSON.stringify(settings) !== JSON.stringify(last);

      if (settingsChanged) {
        node.data.setData((oldData) => ({
          ...oldData,
          output: {
            ...oldData?.output,
            settings,
          },
        }));
        lastPersistedSettingsRef.current = settings;
      }
    } catch (error) {
      console.error("Error persisting Excel export settings:", error);
    }
  }, [
    node?.data?.setData,
    fieldsPerInput,
    upstreamSources,
    availableFields,
    exportFormat,
    downloadAsFile,
    filenameTemplate,
    includeHeaders,
    autoFormat,
    applyStyles,
  ]);

  // Update settings when they change
  useEffect(() => {
    if (!open || !node?.id || !initializedRef.current) return;
    updateNodeSettings();
  }, [
    fieldsPerInput,
    exportFormat,
    downloadAsFile,
    filenameTemplate,
    includeHeaders,
    autoFormat,
    applyStyles,
    updateNodeSettings,
  ]);

  // Debug: Track when parentSelectedInputId changes
  useEffect(() => {
    console.log("ExcelExport: parentSelectedInputId changed", {
      parentSelectedInputId,
      selectedInputId,
      upstreamSourcesCount: upstreamSources.length,
      availableFieldsCount: availableFields.length,
    });
  }, [parentSelectedInputId, selectedInputId, upstreamSources.length, availableFields.length]);
  console.log("--------------", node);
  return (
    <Box>
      {upstreamSources.length === 0 && (
        <Typography variant="body2" sx={{ color: "#888", textAlign: "center", p: 2 }}>
          Please connect an input node to configure export settings
        </Typography>
      )}
      {upstreamSources.length > 0 && (
        <>
          <Box sx={{ mb: 3 }}>
            {/* Fields Selection */}
            <Box
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}
            >
              <Typography variant="subtitle2">Fields to include in export</Typography>
              <Typography variant="caption" color="text.secondary">
                {currentFieldsToInclude.includes("ALL")
                  ? `All ${availableFields.length} fields selected`
                  : `${currentFieldsToInclude.length}/${availableFields.length} fields selected`}
              </Typography>
            </Box>

            {availableFields.length === 0 ? (
              <Box sx={{ p: 2, bgcolor: "rgba(0,0,0,0.1)", borderRadius: 1, mb: 3 }}>
                <Typography variant="body2" color="warning.main">
                  {upstreamSources.length === 0
                    ? "Please connect an input node to configure export settings"
                    : "No fields detected from the selected input. Make sure the upstream node has been executed and provides data with a schema or results."}
                </Typography>
              </Box>
            ) : (
              <Autocomplete
                multiple
                id="fields-to-include"
                options={[
                  { key: "ALL", label: "🔘 All Fields", type: "special" },
                  ...availableFields,
                ]}
                getOptionLabel={(option) => {
                  if (option.key === "ALL") return "🔘 All Fields";
                  return option.label || option.key || option;
                }}
                isOptionEqualToValue={(option, value) => {
                  // Handle comparison between objects and strings
                  const optionKey = typeof option === "object" ? option.key : option;
                  const valueKey = typeof value === "object" ? value.key : value;
                  return optionKey === valueKey;
                }}
                value={currentFieldsToInclude.map((field) => {
                  if (field === "ALL")
                    return { key: "ALL", label: "🔘 All Fields", type: "special" };
                  const found = availableFields.find((f) => f.key === field);
                  return found || { key: field, label: field, type: "string" };
                })}
                onChange={handleFieldsChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    fullWidth
                    placeholder={`Select fields (${availableFields.length} available)`}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#2A2A2A",
                        color: "#CCC",
                      },
                    }}
                  />
                )}
                sx={{ mb: 3 }}
                disableCloseOnSelect
              />
            )}
            {/* Export format */}
            <FormControl fullWidth variant="outlined" sx={{ mb: 3 }}>
              <InputLabel id="export-format-label">Export Format</InputLabel>
              <Select
                labelId="export-format-label"
                id="export-format"
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value)}
                label="Export Format"
                sx={{
                  backgroundColor: "#2A2A2A",
                  color: "#CCC",
                }}
              >
                {exportFormats.map((format) => (
                  <MenuItem key={format.value} value={format.value}>
                    {format.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Download as file switch */}
            <FormGroup sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={downloadAsFile}
                    onChange={(e) => setDownloadAsFile(e.target.checked)}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#FF6C58",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: "#FF6C58",
                      },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography>Download as file</Typography>
                    <Tooltip title="When enabled, generates a downloadable file. When disabled, returns file content as a base64 string in the node output.">
                      <IconButton size="small" sx={{ ml: 0.5 }}>
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
              />
              <FormHelperText sx={{ ml: 2, color: "#999" }}>
                {downloadAsFile
                  ? "File will be available for download when the node executes"
                  : "File content will be passed as output to the next node"}
              </FormHelperText>
            </FormGroup>
            {/* Filename template */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Filename Template
            </Typography>
            <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
              <TextField
                fullWidth
                variant="outlined"
                value={filenameTemplate}
                onChange={(e) => setFilenameTemplate(e.target.value)}
                placeholder="export-{datetime}"
                sx={{
                  mr: 1,
                  backgroundColor: "#2A2A2A",
                  "& .MuiOutlinedInput-root": {
                    color: "#CCC",
                  },
                }}
              />
              <FormControl sx={{ minWidth: 150 }}>
                <Select
                  displayEmpty
                  value=""
                  onChange={(e) => {
                    insertPlaceholder(e.target.value);
                    e.target.value = "";
                  }}
                  sx={{
                    backgroundColor: "#2A2A2A",
                    color: "#CCC",
                  }}
                >
                  <MenuItem value="" disabled>
                    Add placeholder
                  </MenuItem>
                  {filenamePlaceholders.map((placeholder) => (
                    <MenuItem key={placeholder.value} value={placeholder.value}>
                      {placeholder.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <FormHelperText sx={{ mb: 3, color: "#999" }}>
              Use placeholders to create dynamic filenames. Extension will be added automatically.
            </FormHelperText>
            {/* Excel-specific options */}
            {exportFormat === "excel" && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Excel Options
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={includeHeaders}
                        onChange={(e) => setIncludeHeaders(e.target.checked)}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#FF6C58",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "#FF6C58",
                          },
                        }}
                      />
                    }
                    label="Include Headers"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={autoFormat}
                        onChange={(e) => setAutoFormat(e.target.checked)}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#FF6C58",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "#FF6C58",
                          },
                        }}
                      />
                    }
                    label="Auto-format values (dates, numbers, etc.)"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={applyStyles}
                        onChange={(e) => setApplyStyles(e.target.checked)}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#FF6C58",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "#FF6C58",
                          },
                        }}
                      />
                    }
                    label="Apply styling to header row"
                  />
                </FormGroup>
              </Box>
            )}
            {/* CSV-specific options */}
            {exportFormat === "csv" && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  CSV Options
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={includeHeaders}
                        onChange={(e) => setIncludeHeaders(e.target.checked)}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#FF6C58",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "#FF6C58",
                          },
                        }}
                      />
                    }
                    label="Include Headers"
                  />
                </FormGroup>
              </Box>
            )}

            {error && (
              <Typography color="error" sx={{ mt: 2 }}>
                {error}
              </Typography>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};
